'use client';

import { FirebaseApp, FirebaseOptions, getApps, initializeApp } from 'firebase/app';
import { Auth, getAuth, signInWithCustomToken, UserCredential } from 'firebase/auth';
import { getMessaging, getToken, Messaging, onMessage } from 'firebase/messaging';
import { FirebaseStorage, getStorage } from 'firebase/storage';
import { createContext, useContext, useEffect, useState } from 'react';
import { z } from 'zod';

import { getFirebaseToken } from '@/app/user/user-actions';

const firebaseOptionsSchema = z.object({
  apiKey: z.string().default(process.env.NEXT_PUBLIC_FIREBASE_API_KEY ?? ''),
  authDomain: z.string().default(process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN ?? ''),
  databaseURL: z.string().default(process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL ?? ''),
  projectId: z.string().default(process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID ?? ''),
  storageBucket: z.string().default(process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET ?? ''),
  messagingSenderId: z.string().default(process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID ?? ''),
  appId: z.string().default(process.env.NEXT_PUBLIC_FIREBASE_APP_ID ?? ''),
  measurementId: z.string().default(process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID ?? ''),
});
type FirebaseValue = {
  app?: FirebaseApp;
  auth?: Auth;
  messaging?: Messaging;
  storage?: FirebaseStorage;
  user?: UserCredential['user'];
};

const DEFAULT_FIREBASE_VALUE: FirebaseValue = {};

const FirebaseContext = createContext(DEFAULT_FIREBASE_VALUE);

export function useFirebase() {
  return useContext(FirebaseContext);
}

type FirebaseProviderProps = {
  children: React.ReactNode;
  firebaseOptions?: FirebaseOptions;
  name?: string;
};

export function FirebaseProvider({ children, firebaseOptions = {}, name = '[DEFAULT]' }: FirebaseProviderProps) {
  const [app, setApp] = useState<FirebaseValue['app']>(DEFAULT_FIREBASE_VALUE.app);
  const [auth, setAuth] = useState<FirebaseValue['auth']>(DEFAULT_FIREBASE_VALUE.auth);
  const [messaging, setMessaging] = useState<FirebaseValue['messaging']>(DEFAULT_FIREBASE_VALUE.messaging);
  const [storage, setStorage] = useState<FirebaseValue['storage']>(DEFAULT_FIREBASE_VALUE.storage);
  const [user, setUser] = useState<FirebaseValue['user']>();

  useEffect(() => {
    const apps = getApps();
    const existingApp = apps.find((app) => app.name === name);
    const app = existingApp || initializeApp(firebaseOptionsSchema.parse(firebaseOptions));
    const auth = getAuth(app);
    const messaging = getMessaging(app);
    const storage = getStorage(app);

    setApp(app);
    setAuth(auth);
    setMessaging(messaging);
    setStorage(storage);

    (async () => {
      const token = await getFirebaseToken();
      if (token) {
        const credential = await signInWithCustomToken(auth, token);

        if (credential) {
          setUser(credential.user);
        }
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FirebaseContext.Provider value={{ app, auth, messaging, storage, user }}>
      <>{children}</>
    </FirebaseContext.Provider>
  );
}
